import {useEffect, useState} from 'react';

import {useScreenSize} from '@nfq/react-grid';
import {HTTP_METHODS} from '@nfq/typed-next-api';
import {useRouter} from 'next/router';

import {usePageViewTracking} from 'Application/useCases/useTracking';

import type {Format, Funnels, Motiv} from 'Domain/entities/tracking';

/**
 * Custom hook that tracks page views and logs them using the `trackPageView` function.
 * This hook is called whenever the route changes, indicating a new page view, and tracks additional details such as
 * whether it is the first view, the referrer URL, and any URL parameters related to media referrals (e.g., format, funnel, motive, page).
 * The screen size is also tracked using the `useScreenSize` hook, providing information about the user's viewport.
 * The page view is logged via an HTTP POST request, ensuring that the tracking data is sent to the server.
 *
 * @example
 * ```tsx
 * const MyPage = () => {
 *   useTrackingPageView();
 *
 *   return (
 *     <div>
 *       <h1>Welcome to My Page</h1>
 *     </div>
 *   );
 * };
 * ```
 */
export const useTrackingPageView = () => {
    const [firstView, setFirstView] = useState(true);
    const {trackPageView} = usePageViewTracking();
    const {route} = useRouter();
    const screen = useScreenSize();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        void trackPageView({
            body: {
                firstView,
                referrer: document.referrer,
                ...(params.size > 0 ? {
                    referringMedia: {
                        format: params.get('format') as Format,
                        funnel: params.get('funnel') as Funnels,
                        motive: params.get('motiv') as Motiv,
                        page: params.get('page')
                    }
                } : {}),
                url: route,
                viewport: screen
            },
            method: HTTP_METHODS.POST
        });

        if (firstView) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setFirstView(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route]);
};